import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FaMapMarkerAlt,
  FaRoad,
  FaClock,
  FaRupeeSign,
  FaBell,
  FaSync,
  FaUser,
  FaPhone,
  FaCar,
  FaCheckCircle,
  FaTimesCircle,
  FaCalendarAlt,
} from "react-icons/fa";
import DriverLayout from "../../layouts/DriverLayout";
import socketService from "../../utils/socketService";
import { getDriverAuthHeaders } from "../../utils/driverAuthUtils";
import { API_URL } from "../../config/apiConfig";

/* ─── Single Ride Card (all details inline, no modal) ─────────────────── */
const RideCard = ({ ride, onAccepted, onDeclined }) => {
  const navigate = useNavigate();
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);

  const cabTypeName =
    typeof ride.cabType === "object"
      ? ride.cabType?.name || "Unknown"
      : ride.cabType || "N/A";

  const handleAccept = async () => {
    if (!ride.bookingId) { toast.error("Booking ID missing"); return; }
    setAccepting(true);
    try {
      const headers = getDriverAuthHeaders();
      const { data } = await axios.post(
        `${API_URL}/booking/${ride.bookingId}/accept-ride`,
        {},
        { headers }
      );
      if (data.success) {
        toast.success("✅ Ride accepted! Heading to passenger...");
        onAccepted(ride.bookingId);
        if (typeof window !== 'undefined') {
          sessionStorage.setItem("selectedBookingId", ride.bookingId);
        }
        navigate("/driver/current-ride");
      } else {
        toast.error(data.message || "Failed to accept ride");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to accept ride");
    } finally {
      setAccepting(false);
    }
  };

  const handleDecline = () => {
    setDeclining(true);
    toast.info("Ride declined");
    onDeclined(ride.bookingId);
    setDeclining(false);
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-100 shadow-md overflow-hidden">
      {/* Card top bar */}
      <div className="flex items-center justify-between px-6 py-3 bg-gradient-to-r from-emerald-500 to-green-600">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
          </span>
          <span className="text-white font-extrabold text-sm tracking-wide">
            🚖 New Ride Available
          </span>
        </div>
        {ride.bookingNumber && (
          <span className="text-emerald-100 text-xs font-semibold">
            #{ride.bookingNumber}
          </span>
        )}
      </div>

      <div className="p-6 space-y-5">
        {/* Route — Pickup → Drop */}
        <div className="space-y-0">
          {/* Pickup */}
          <div className="flex items-start gap-4">
            <div className="flex flex-col items-center pt-1">
              <div className="w-9 h-9 rounded-full bg-red-50 border-2 border-red-200 flex items-center justify-center flex-shrink-0">
                <FaMapMarkerAlt className="text-red-500 text-sm" />
              </div>
              <div className="w-0.5 h-8 bg-dashed bg-slate-200 border-l-2 border-dashed border-slate-300 my-1" />
            </div>
            <div className="pb-4">
              <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-0.5">
                Pickup
              </p>
              <p className="text-sm font-bold text-slate-800 leading-snug">
                {ride.pickup?.address || "Location not specified"}
              </p>
            </div>
          </div>

          {/* Drop */}
          <div className="flex items-start gap-4">
            <div className="w-9 h-9 rounded-full bg-green-50 border-2 border-green-200 flex items-center justify-center flex-shrink-0">
              <FaMapMarkerAlt className="text-green-500 text-sm" />
            </div>
            <div>
              <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-0.5">
                Drop
              </p>
              <p className="text-sm font-bold text-slate-800 leading-snug">
                {ride.drop?.address || "Location not specified"}
              </p>
            </div>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-blue-50 rounded-2xl p-4 text-center border border-blue-100">
            <FaRoad className="text-blue-500 mx-auto mb-1.5 text-base" />
            <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Distance</p>
            <p className="text-lg font-black text-slate-800">
              {ride.distance ? `${ride.distance} km` : "N/A"}
            </p>
          </div>
          <div className="bg-purple-50 rounded-2xl p-4 text-center border border-purple-100">
            <FaClock className="text-purple-500 mx-auto mb-1.5 text-base" />
            <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Duration</p>
            <p className="text-lg font-black text-slate-800">
              {ride.duration ? `${Math.round(ride.duration / 60)} min` : "N/A"}
            </p>
          </div>
          <div className="bg-emerald-50 rounded-2xl p-4 text-center border border-emerald-100">
            <FaRupeeSign className="text-emerald-500 mx-auto mb-1.5 text-base" />
            <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Fare</p>
            <p className="text-lg font-black text-emerald-700">
              ₹{ride.fare ? Math.round(ride.fare) : "N/A"}
            </p>
          </div>
        </div>

        {/* Schedule & Round Trip Return Date/Time */}
        {(ride.returnDate || ride.pickupDate || ride.pickupTime || ride.tripType === "roundTrip" || ride.journeyType === "roundTrip") && (
          <div className="flex flex-wrap gap-2.5 p-3 bg-slate-50 border border-slate-100 rounded-2xl text-xs">
            {(ride.pickupDate || ride.pickupTime) && (
              <div className="flex items-center gap-1.5 text-slate-700 font-semibold">
                <FaCalendarAlt className="text-slate-400 text-xs" />
                <span>Pickup: {ride.pickupDate ? new Date(ride.pickupDate).toLocaleDateString() : ""} {ride.pickupTime ? `at ${ride.pickupTime}` : ""}</span>
              </div>
            )}
            {(ride.returnDate || ride.tripType === "roundTrip" || ride.journeyType === "roundTrip") && ride.returnDate && (
              <div className="flex items-center gap-1.5 text-indigo-700 font-bold bg-indigo-50/80 px-2.5 py-1 rounded-lg border border-indigo-100">
                <FaCalendarAlt className="text-indigo-500 text-xs" />
                <span>Round Trip Return: {new Date(ride.returnDate).toLocaleDateString()} {ride.returnTime ? `at ${ride.returnTime}` : ""}</span>
              </div>
            )}
          </div>
        )}

        {/* Extra details row */}
        <div className="flex flex-wrap gap-3">
          {/* Cab Type */}
          {cabTypeName && cabTypeName !== "N/A" && (
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-100 rounded-xl">
              <FaCar className="text-amber-500 text-sm" />
              <span className="text-sm font-bold text-slate-700">{cabTypeName}</span>
            </div>
          )}

          {/* Passenger Name */}
          {ride.passenger?.name && (
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl">
              <FaUser className="text-slate-400 text-sm" />
              <span className="text-sm font-bold text-slate-700">{ride.passenger.name}</span>
            </div>
          )}

          {/* Passenger Phone */}
          {ride.passenger?.phone && (
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl">
              <FaPhone className="text-slate-400 text-sm" />
              <span className="text-sm font-bold text-slate-700">{ride.passenger.phone}</span>
            </div>
          )}
        </div>

        {/* Selected Add-on Services */}
        {ride.selectedAddOns && ride.selectedAddOns.length > 0 && (
          <div className="p-3 bg-slate-50 border border-slate-100 rounded-2xl">
            <p className="text-[10px] font-black text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1">
              <span>📦</span> Selected Add-on Services
            </p>
            <div className="space-y-1.5">
              {ride.selectedAddOns.map((addon, index) => (
                <div key={index} className="flex justify-between items-center text-xs font-bold text-gray-700">
                  <span className="truncate pr-2">{addon.name}</span>
                  <span className="text-gray-900 font-extrabold flex-shrink-0">₹{addon.price}</span>
                </div>
              ))}
            </div>
          </div>
        )}
 
        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-3 pt-1">
          <button
            onClick={handleDecline}
            disabled={accepting || declining}
            className="flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-extrabold text-sm transition-all duration-150 active:scale-95 disabled:opacity-50"
          >
            <FaTimesCircle className="text-slate-400" />
            {declining ? "Declining..." : "Decline"}
          </button>
          <button
            onClick={handleAccept}
            disabled={accepting || declining}
            className="flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white font-extrabold text-sm transition-all duration-150 active:scale-95 disabled:opacity-50 shadow-lg shadow-emerald-200"
          >
            {accepting ? (
              <>
                <div className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                Accepting...
              </>
            ) : (
              <>
                <FaCheckCircle />
                Accept Ride
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Main Page ────────────────────────────────────────────────────────── */
const NewRideRequests = () => {
  const { driver: driverAuth } = useSelector((state) => state.driverAuth);
  const { driver } = useSelector((state) => state.driver);

  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const driverId =
    driver?._id ||
    driverAuth?._id ||
    (driverAuth?.driver && driverAuth.driver._id);

  const syncBadge = (list) => {
    window.dispatchEvent(
      new CustomEvent("pendingRidesUpdate", { detail: { count: list.length } })
    );
  };

  const fetchRides = async (silent = false) => {
    if (!driverId) return;
    if (!silent) setLoading(true);
    else setRefreshing(true);

    const token = localStorage.getItem("driverToken");
    if (!token) { setLoading(false); return; }

    try {
      const { data } = await axios.get(
        `${API_URL}/driver/${driverId}/pending-ride-requests`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data.success) {
        setRides(data.data || []);
        syncBadge(data.data || []);
      }
    } catch {
      if (!silent) toast.error("Failed to load ride requests");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchRides();
    const interval = setInterval(() => fetchRides(true), 15000);

    // Socket: new ride → add to list
    const cleanNew = socketService.onNewRideRequest((data) => {
      setRides((prev) => {
        if (prev.some((r) => r.bookingId === data.bookingId)) return prev;
        const updated = [data, ...prev];
        syncBadge(updated);
        toast.info("🚖 New ride request arrived!", { autoClose: 3000 });
        return updated;
      });
    });

    // Socket: ride taken by another driver
    const cleanTaken = socketService.onRideTaken((data) => {
      if (data?.bookingId) {
        setRides((prev) => {
          const updated = prev.filter((r) => r.bookingId !== data.bookingId);
          syncBadge(updated);
          return updated;
        });
        toast.warning("A ride was taken by another driver", { autoClose: 4000 });
      }
    });

    // Socket: auto-assign expired
    const cleanExpired = socketService.onAutoAssignExpired?.((data) => {
      if (data?.bookingId) {
        setRides((prev) => {
          const updated = prev.filter((r) => r.bookingId !== data.bookingId);
          syncBadge(updated);
          return updated;
        });
      }
    }) || (() => {});

    return () => {
      clearInterval(interval);
      cleanNew();
      cleanTaken();
      cleanExpired();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverId]);

  const handleAccepted = (bookingId) => {
    setRides((prev) => {
      const updated = prev.filter((r) => r.bookingId !== bookingId);
      syncBadge(updated);
      return updated;
    });
  };

  const handleDeclined = (bookingId) => {
    setRides((prev) => {
      const updated = prev.filter((r) => r.bookingId !== bookingId);
      syncBadge(updated);
      return updated;
    });
  };

  return (
    <DriverLayout>
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-11 h-11 rounded-2xl bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-200">
              <FaBell className="text-white text-lg" />
            </div>
            {rides.length > 0 && (
              <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-[10px] font-extrabold flex items-center justify-center animate-bounce">
                {rides.length}
              </span>
            )}
          </div>
          <div>
            <h1 className="text-xl font-black text-slate-800">New Ride Requests</h1>
            <p className="text-sm text-slate-500">
              {loading
                ? "Loading..."
                : `${rides.length} ride${rides.length !== 1 ? "s" : ""} waiting for you`}
            </p>
          </div>
        </div>
        <button
          onClick={() => fetchRides(true)}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold text-sm transition-all disabled:opacity-50"
        >
          <FaSync className={refreshing ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* States */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 gap-4">
          <div className="w-12 h-12 rounded-full border-4 border-emerald-500 border-t-transparent animate-spin" />
          <p className="text-slate-500 font-medium">Checking for ride requests...</p>
        </div>
      ) : rides.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
          <div className="w-20 h-20 rounded-3xl bg-slate-100 flex items-center justify-center text-4xl">
            🚗
          </div>
          <h2 className="text-lg font-black text-slate-700">No Pending Ride Requests</h2>
          <p className="text-slate-400 text-sm max-w-xs">
            Make sure you are set to <strong>Online</strong>. New requests appear here automatically.
          </p>
          <button
            onClick={() => fetchRides()}
            className="mt-2 px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl text-sm transition-all"
          >
            Check Again
          </button>
        </div>
      ) : (
        <div className="space-y-5">
          {rides.map((ride, idx) => (
            <RideCard
              key={ride.bookingId || idx}
              ride={ride}
              onAccepted={handleAccepted}
              onDeclined={handleDeclined}
            />
          ))}
        </div>
      )}
    </DriverLayout>
  );
};

export default NewRideRequests;
